import React, { useState, useEffect } from 'react';
import {
  Container,
  Box,
  TextField,
  Grid,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  InputAdornment,
  Typography,
} from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon,
  Security as SecurityIcon,
  Key as KeyIcon,
  People as PeopleIcon,
  CheckCircle as ActiveIcon,
  Cancel as InactiveIcon,
} from '@mui/icons-material';
import rolesService from '../../../services/rolesService';
import RoleFormModal from '../../../components/RoleFormModal';
import { showSuccess, showError, showDeleteConfirmation } from '../../../utils/sweetAlertConfig';
import PageHeader from '../../../components/PageHeader';
import ModernTable from '../../../components/ModernTable';
import StatsCard from '../../../components/StatsCard';

const PRIMARY = "#29204C";
const SUCCESS = "#68B977";
const SECONDARY = "#5A6D8B";

export default function Roles() {
  const [roles, setRoles] = useState([]);
  const [allRoles, setAllRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [formModalOpen, setFormModalOpen] = useState(false);
  const [selectedRole, setSelectedRole] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [roleToDelete, setRoleToDelete] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(15);

  // Stats state
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    inactive: 0,
    withPermissions: 0,
  });

  useEffect(() => {
    loadRoles();
  }, []);

  const loadRoles = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await rolesService.getRoles({ search });
      if (response.success) {
        const rolesData = response.roles || [];
        setRoles(rolesData);
        setAllRoles(rolesData);
        calculateStats(rolesData);
      }
    } catch (error) {
      console.error('Error loading roles:', error);
      setError('Failed to load roles');
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = (rolesData) => {
    setStats({
      total: rolesData.length,
      active: rolesData.filter(r => r.is_active).length,
      inactive: rolesData.filter(r => !r.is_active).length,
      withPermissions: rolesData.filter(r => r.permissions && r.permissions.length > 0).length,
    });
  };

  useEffect(() => {
    if (!search) {
      setRoles(allRoles);
    } else {
      const val = search.toLowerCase();
      setRoles(allRoles.filter(r =>
        (r.name && r.name.toLowerCase().includes(val)) ||
        (r.display_name && r.display_name.toLowerCase().includes(val)) ||
        (r.description && r.description.toLowerCase().includes(val))
      ));
    }
    setPage(0);
  }, [search, allRoles]);

  const handleCreateRole = () => {
    setSelectedRole(null);
    setFormModalOpen(true);
  };

  const handleEditRole = (row) => {
    setSelectedRole(row);
    setFormModalOpen(true);
  };

  const handleCloseFormModal = () => {
    setFormModalOpen(false);
    setSelectedRole(null);
  };

  const handleRoleSaved = () => {
    showSuccess('Success!', 'Role saved successfully!');
    loadRoles();
    handleCloseFormModal();
  };

  const handleDeleteClick = (row) => {
    setRoleToDelete(row);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!roleToDelete) return;

    try {
      const response = await rolesService.deleteRole(roleToDelete.id);
      if (response.success) {
        showSuccess('Deleted!', 'Role deleted successfully!');
        loadRoles();
      }
    } catch (error) {
      console.error('Error deleting role:', error);
      showError('Error!', error.response?.data?.message || 'Failed to delete role');
    } finally {
      setDeleteDialogOpen(false);
      setRoleToDelete(null);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setRoleToDelete(null);
  };

  const handleDelete = (row) => {
    if (row.name === 'super_admin') {
      showError('Error!', 'Cannot delete super admin role');
      return;
    }
    handleDeleteClick(row);
  };

  const handleEdit = (row) => {
    if (row.name === 'super_admin') {
      showError('Error!', 'Cannot edit super admin role');
      return;
    }
    handleEditRole(row);
  };

  const columns = [
    {
      label: "Role Name",
      field: "name",
      width: "180px",
      render: (row) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <SecurityIcon fontSize="small" sx={{ color: SECONDARY }} />
          <Typography variant="body2" sx={{ fontWeight: 600, fontFamily: 'monospace' }}>
            {row.name}
          </Typography>
        </Box>
      ),
    },
    {
      label: "Display Name",
      field: "display_name",
      render: (row) => (
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {row.display_name}
        </Typography>
      ),
    },
    {
      label: "Description",
      field: "description",
      render: (row) => (
        <Typography variant="body2" color="text.secondary">
          {row.description || '-'}
        </Typography>
      ),
    },
    {
      label: "Permissions",
      field: "permissions",
      align: "center",
      width: "130px",
      render: (row) => (
        <Chip
          icon={<KeyIcon fontSize="small" />}
          label={`${row.permissions?.length || 0}`}
          sx={{
            bgcolor: `${PRIMARY}15`,
            color: PRIMARY,
            fontWeight: 600,
          }}
          size="small"
        />
      ),
    },
    {
      label: "Users",
      field: "users",
      align: "center",
      width: "100px",
      render: (row) => (
        <Chip
          icon={<PeopleIcon fontSize="small" />}
          label={`${row.users?.length || 0}`}
          sx={{
            bgcolor: `${SECONDARY}15`,
            color: SECONDARY,
            fontWeight: 600,
          }}
          size="small"
        />
      ),
    },
    {
      label: "Status",
      field: "is_active",
      align: "center",
      width: "120px",
      render: (row) => (
        <Chip
          label={row.is_active ? 'ACTIVE' : 'INACTIVE'}
          sx={{
            bgcolor: row.is_active ? SUCCESS : '#6c757d',
            color: '#fff',
            fontWeight: 600,
            fontSize: '0.75rem',
          }}
          size="small"
        />
      ),
    },
  ];

  const paginatedRoles = roles.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <PageHeader
        title="Roles Management"
        subtitle="Manage user roles and their permissions"
        icon={SecurityIcon}
        breadcrumbs={[
          { label: 'Dashboard', link: '/dashboard' },
          { label: 'Security' },
          { label: 'Roles' }
        ]}
        action={{
          label: 'Create Role',
          icon: <AddIcon />,
          onClick: handleCreateRole,
          variant: 'contained',
          color: 'success',
        }}
      />

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            title="Total Roles"
            value={stats.total}
            icon={SecurityIcon}
            color={PRIMARY}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            title="Active Roles"
            value={stats.active}
            icon={ActiveIcon}
            color={SUCCESS}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            title="Inactive Roles"
            value={stats.inactive}
            icon={InactiveIcon}
            color="#6c757d"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            title="With Permissions"
            value={stats.withPermissions}
            icon={KeyIcon}
            color="#2196f3"
          />
        </Grid>
      </Grid>

      {/* Search */}
      <Box sx={{ mb: 3 }}>
        <TextField
          fullWidth
          placeholder="Search by role name, display name, or description..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{
            maxWidth: 400,
            '& .MuiOutlinedInput-root': {
              bgcolor: '#fff',
            }
          }}
        />
      </Box>

      {/* Table */}
      <ModernTable
        columns={columns}
        data={paginatedRoles}
        loading={loading}
        page={page}
        rowsPerPage={rowsPerPage}
        totalRows={roles.length}
        onPageChange={setPage}
        onRowsPerPageChange={(newRowsPerPage) => {
          setRowsPerPage(newRowsPerPage);
          setPage(0);
        }}
        onEdit={handleEdit}
        onDelete={handleDelete}
        emptyMessage={error || "No roles found"}
      />

      {/* Role Form Modal */}
      <RoleFormModal
        open={formModalOpen}
        onClose={handleCloseFormModal}
        role={selectedRole}
        onRoleSaved={handleRoleSaved}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        PaperProps={{
          sx: {
            borderRadius: 2,
          }
        }}
      >
        <DialogTitle
          sx={{
            bgcolor: '#dc3545',
            color: '#ffffff',
            fontWeight: 700,
            fontSize: '1.25rem',
          }}
        >
          Delete Role
        </DialogTitle>
        <DialogContent sx={{ mt: 3 }}>
          <Typography>
            Are you sure you want to delete the role <strong>{roleToDelete?.display_name}</strong>?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 2 }}>
          <Button
            onClick={handleDeleteCancel}
            variant="outlined"
            sx={{ color: SECONDARY, borderColor: SECONDARY }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            variant="contained"
            sx={{
              bgcolor: '#dc3545',
              '&:hover': { bgcolor: '#c82333' },
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
